import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { ToastProvider } from '@xcentral/ui';
import { getMsalInstance } from '../auth/msalInstance';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { ScrollToTop } from '../components/ScrollToTop';
import { BrandProvider, StaticBrandProvider } from '../theme/BrandProvider';

export function RootLayout() {
  // Bug #16825 — the FIRST client render must reproduce what the server rendered,
  // or hydration fails for the whole root.
  //
  // The trap: main.tsx awaits initializeMsalInstance() BEFORE hydrating, because
  // handleRedirectPromise() must resolve before React renders (CLAUDE.md, MSAL
  // pattern — that ordering is not negotiable and is NOT what changed here). So by
  // the time hydration ran, getMsalInstance() was already non-null and this layout
  // took the provider branch — while the prerendered HTML had taken the null branch.
  // Two different trees, so React reported "Expected server HTML to contain a
  // matching <div> in <div>" (#418, once per differing node) and then #423, which
  // discards the entire server-rendered root and re-renders it client-side.
  //
  // The fix is to defer the UPGRADE, not the MSAL initialisation: render #1 takes
  // the same branch the server took, hydration matches, and the provider tree
  // arrives on render #2 as an ordinary update. MSAL is still fully initialised
  // before any of this — only the moment its provider mounts has moved.
  //
  // Consequence, stated rather than hidden: switching StaticBrandProvider ->
  // BrandProvider changes the component type, so React remounts the subtree below
  // it once. That was already happening — via the far more expensive #423 path —
  // so this is strictly better, but it is not free.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    setHydrated(true);
  }, []);

  const instance = hydrated ? getMsalInstance() : null;

  const content = (
    <>
      <ScrollToTop />
      <Outlet />
    </>
  );

  if (!instance) {
    // SSG render (Node), and the first client render for hydration parity.
    // No MSAL/Toast context. Auth-aware UI inside descendants must guard with
    // <ClientOnly>. Brand defaults to Conscierra (StaticBrandProvider never fetches).
    return (
      <ErrorBoundary>
        <StaticBrandProvider>{content}</StaticBrandProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <MsalProvider instance={instance}>
        <ToastProvider>
          <BrandProvider>{content}</BrandProvider>
        </ToastProvider>
      </MsalProvider>
    </ErrorBoundary>
  );
}
